import { type Ref, type ComponentPropsWithoutRef, type ReactNode, type ComponentProps } from 'react'
import styled from 'styled-components'
import { Button, IconButton, TextButton } from '@shipt/design-system-buttons'
import { NextLink, type NextLinkProps } from '@/features/shared/elements/Link'
import { Link, type TypographyProps } from '@shipt/design-system-typography'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { getLinkNewTabAttributes } from '@/features/shared/elements/Link/utils'
import { routes } from '@shared/constants/routes'
import { pciRouteMapByURL } from '@shared/constants/routes.pci'

type LinkProps = NextLinkProps & {
  opensInNewTab?: boolean
  ref?: Ref<HTMLAnchorElement>
}

const nativeLinkPrefixes = [routes.LOGIN.url, routes.SIGN_UP.url, ...pciRouteMapByURL.keys()]
// nativeLink is needed for login, signup, and PCI route link buttons (e.g. checkout)
// which navigate outside of the Next.js router. use of NextLink in these scenarios
// results in 404s to our webserver due to wl gateway config.
// nativeLink prop can also be used explicitly by any route that needs a native anchor tag.
// See this CU ticket for context - https://app.clickup.com/t/86b4pca0g
type ButtonProps = ComponentPropsWithoutRef<typeof Button> & LinkProps & { nativeLink?: boolean }

export const ButtonLink = ({
  href,
  replace,
  shallow,
  opensInNewTab = false,
  nativeLink,
  ...restProps
}: ButtonProps) => {
  const isNativeLink = nativeLink || nativeLinkPrefixes.some((prefix) => href.startsWith(prefix))
  return (
    <Button
      {...restProps}
      forwardedAs={isNativeLink ? 'a' : NextLink}
      {...getLinkNewTabAttributes(opensInNewTab)}
      isLink
      href={href}
      replace={replace}
      shallow={shallow}
    />
  )
}

type TextButtonProps = ComponentPropsWithoutRef<typeof TextButton> & LinkProps

export const TextButtonLink = ({
  href,
  shallow,
  opensInNewTab = false,
  ...restProps
}: TextButtonProps) => {
  return (
    <TextButton
      {...restProps}
      {...getLinkNewTabAttributes(opensInNewTab)}
      isLink
      forwardedAs={NextLink}
      href={href}
      shallow={shallow}
    />
  )
}
type IconButtonProps = ComponentProps<typeof IconButton> & LinkProps
export const IconButtonLink = ({ href, opensInNewTab = false, ...restProps }: IconButtonProps) => {
  return (
    <IconButton
      {...restProps}
      {...getLinkNewTabAttributes(opensInNewTab)}
      isLink
      forwardedAs={NextLink}
      href={href}
    />
  )
}

export const ExternalButton = ({
  children,
  ...props
}: TypographyProps & { children: ReactNode }) => (
  <ExternalLink {...props} as={TransparentButton}>
    {children}
  </ExternalLink>
)

const ExternalLink = styled(Link)`
  font-weight: normal;
  white-space: nowrap;
  align-self: start;
`
